import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Accordion } from 'react-bootstrap';
import { toast } from 'react-toastify';

const ICON_OPTIONS = [
  'ri-customer-service-2-line', 'ri-mail-line', 'ri-map-pin-2-line', 'ri-time-line',
  'ri-phone-line', 'ri-whatsapp-line', 'ri-building-line', 'ri-global-line',
  'ri-facebook-fill', 'ri-instagram-line', 'ri-linkedin-fill', 'ri-youtube-fill',
  'ri-headphone-line', 'ri-chat-1-line', 'ri-send-plane-line', 'ri-information-line',
];

const emptyInfoCard = () => ({ icon: 'ri-phone-line', title: '', value: '' });
const emptySection  = () => ({ label: '', title: '', description: '', image: '' });

// ── ALL HELPER COMPONENTS DEFINED OUTSIDE ContactUsEditor ────────────────────
// Defining them inside causes React to treat them as new component types on
// every render, unmounting/remounting DOM nodes and stealing input focus.

const SectionHeader = ({ label }) => (
  <h6 className="text-primary fw-bold text-uppercase small mb-4" style={{ letterSpacing: '1px' }}>
    {label}
  </h6>
);

const FieldRow = ({ label, children }) => (
  <div className="mb-3">
    <Form.Label className="small fw-bold">{label}</Form.Label>
    {children}
  </div>
);

const AddBtn = ({ onClick, label = 'Add' }) => (
  <Button
    variant="outline-primary"
    size="sm"
    className="mt-2 fw-bold d-flex align-items-center gap-2"
    onClick={onClick}
  >
    <i className="ri-add-circle-line" /> {label}
  </Button>
);

const RemoveBtn = ({ onClick }) => (
  <Button
    variant="outline-danger"
    size="sm"
    onClick={(e) => {
      e.stopPropagation(); // prevent Accordion from intercepting the click
      onClick();
    }}
    style={{ padding: '2px 8px' }}
  >
    <i className="ri-delete-bin-line" />
  </Button>
);

const IconPicker = ({ value, onChange }) => (
  <Form.Select size="sm" value={value} onChange={e => onChange(e.target.value)}>
    {ICON_OPTIONS.map(ic => (
      <option key={ic} value={ic}>
        {ic.replace('ri-', '').replace(/-line$|-fill$/, '').replace(/-/g, ' ')}
      </option>
    ))}
  </Form.Select>
);

const ItemCard = ({ children, onRemove }) => (
  <div className="border rounded p-3 pt-4 mb-3 bg-light position-relative">
    <div style={{ position: 'absolute', top: '8px', right: '10px', zIndex: 10 }}>
      <RemoveBtn onClick={onRemove} />
    </div>
    {children}
  </div>
);

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────

const ContactUsEditor = ({ workingData, updateSection, onSave, isSaving }) => {

  const [cu, setCu] = useState(() => ({
    // Hero
    heroTitle:    'Contact Us',
    heroSubtitle: "We're here to help — reach out to us anytime",
    heroBgImage:  '',
    // Section header
    sectionTitle: 'Get in Touch',
    description:  '',
    // Info Cards
    infoCards: [
      { icon: 'ri-customer-service-2-line', title: 'Customer Support', value: '' },
      { icon: 'ri-mail-line',               title: 'Email Address',    value: '' },
      { icon: 'ri-map-pin-2-line',          title: 'Office Address',   value: '' },
      { icon: 'ri-time-line',               title: 'Working Hours',    value: 'Mon–Sat, 9 AM – 8 PM' },
    ],
    // Socials toggle
    showSocials: true,
    // Map
    mapIframeUrl: '',
    // Form labels
    formTitle:        'Send Us a Message',
    formSuccessTitle: 'Message Sent!',
    formSuccessText:  "We'll get back to you within 24 hours.",
    formButtonText:   'Send Message',
    // Extra
    extraSections: [],
    // DB wins over defaults
    ...workingData.contactUs,
  }));

  // ── pure local setters ────────────────────────────────────────────────────
  const set = (key, val) => setCu(prev => ({ ...prev, [key]: val }));

  const addItem    = (key, empty)           => setCu(prev => ({ ...prev, [key]: [...(prev[key] || []), empty()] }));
  const removeItem = (key, idx)             => setCu(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  const updateItem = (key, idx, field, val) => setCu(prev => {
    const arr = [...(prev[key] || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...prev, [key]: arr };
  });

  // ── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    updateSection('contactUs', cu);
    if (onSave) await onSave(cu);
    toast.success('Contact Us saved successfully!');
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={11}>
          <Card className="shadow-sm border-0 rounded-4">

            <Card.Header className="bg-white py-3 border-bottom">
              <h5 className="fw-bold mb-0">
                <i className="ri-customer-service-2-line me-2 text-primary" />
                Contact Us — Content Editor
              </h5>
            </Card.Header>

            <Card.Body className="p-0">
              <Accordion flush>

                {/* ── 1. HERO ─────────────────────────────────── */}
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <i className="ri-image-2-line me-2 text-primary" />
                    <strong>Hero Section</strong>
                    <span className="ms-2 text-muted small">— Banner title &amp; background</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="1. Hero Banner" />
                    <Row className="g-3">
                      <Col md={6}>
                        <FieldRow label="Hero Title">
                          <Form.Control
                            value={cu.heroTitle}
                            placeholder="Contact Us"
                            onChange={e => set('heroTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Hero Subtitle">
                          <Form.Control
                            value={cu.heroSubtitle}
                            placeholder="We're here to help..."
                            onChange={e => set('heroSubtitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={12}>
                        <FieldRow label="Background Image URL">
                          <Form.Control
                            value={cu.heroBgImage}
                            placeholder="https://images.unsplash.com/..."
                            onChange={e => set('heroBgImage', e.target.value)}
                          />
                          {cu.heroBgImage && (
                            <img
                              src={cu.heroBgImage}
                              alt="preview"
                              className="mt-2 rounded"
                              style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', opacity: 0.85 }}
                              onError={e => e.target.style.display = 'none'}
                            />
                          )}
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 2. SECTION HEADER ───────────────────────── */}
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <i className="ri-text me-2 text-primary" />
                    <strong>Section Header</strong>
                    <span className="ms-2 text-muted small">— Left panel heading &amp; description</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="2. Section Header" />
                    <Row className="g-3">
                      <Col md={4}>
                        <FieldRow label="Heading">
                          <Form.Control
                            value={cu.sectionTitle}
                            placeholder="Get in Touch"
                            onChange={e => set('sectionTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={8}>
                        <FieldRow label="Description (shown below heading)">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={cu.description}
                            placeholder="Have a question or need help planning your trip?..."
                            onChange={e => set('description', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 3. INFO CARDS ───────────────────────────── */}
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    <i className="ri-contacts-line me-2 text-primary" />
                    <strong>Contact Info Cards</strong>
                    <span className="ms-2 text-muted small">— Phone, email, address, hours etc.</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="3. Info Cards" />
                    <p className="text-muted small mb-3">
                      If left empty, phone &amp; email will auto-fill from your Branding settings.
                    </p>
                    {(cu.infoCards || []).map((c, i) => (
                      <ItemCard key={i} onRemove={() => removeItem('infoCards', i)}>
                        <Row className="g-2">
                          <Col md={3}>
                            <Form.Label className="small fw-bold">Icon</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                              <i className={c.icon} style={{ fontSize: '20px', color: '#0d6efd' }} />
                              <IconPicker value={c.icon} onChange={val => updateItem('infoCards', i, 'icon', val)} />
                            </div>
                          </Col>
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Card Title</Form.Label>
                            <Form.Control
                              size="sm"
                              value={c.title}
                              placeholder="Customer Support"
                              onChange={e => updateItem('infoCards', i, 'title', e.target.value)}
                            />
                          </Col>
                          <Col md={5}>
                            <Form.Label className="small fw-bold">Value</Form.Label>
                            <Form.Control
                              size="sm"
                              value={c.value}
                              placeholder="+91 99999 00000"
                              onChange={e => updateItem('infoCards', i, 'value', e.target.value)}
                            />
                          </Col>
                        </Row>
                      </ItemCard>
                    ))}
                    <AddBtn onClick={() => addItem('infoCards', emptyInfoCard)} label="Add Info Card" />
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 4. SOCIAL LINKS ─────────────────────────── */}
                {/* <Accordion.Item eventKey="3">
                  <Accordion.Header>
                    <i className="ri-share-line me-2 text-primary" />
                    <strong>Social Links</strong>
                    <span className="ms-2 text-muted small">— Show/hide socials from Branding</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="4. Social Links" />
                    <div className="d-flex align-items-center gap-3 p-3 border rounded bg-light">
                      <Form.Check
                        type="switch"
                        id="showSocials"
                        checked={cu.showSocials}
                        onChange={e => set('showSocials', e.target.checked)}
                        label={
                          <span className="fw-bold small">
                            {cu.showSocials
                              ? 'Social icons shown on Contact page'
                              : 'Social icons hidden on Contact page'}
                          </span>
                        }
                      />
                    </div>
                    <small className="text-muted d-block mt-2">
                      <i className="ri-information-line me-1" />
                      Social URLs are managed in <strong>Step 1 → Branding &amp; Identity → Social Media</strong>
                    </small>
                  </Accordion.Body>
                </Accordion.Item> */}

                {/* ── 5. CONTACT FORM LABELS ──────────────────── */}
                <Accordion.Item eventKey="4">
                  <Accordion.Header>
                    <i className="ri-mail-send-line me-2 text-primary" />
                    <strong>Contact Form Labels</strong>
                    <span className="ms-2 text-muted small">— Form heading &amp; success message</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="5. Form Labels" />
                    <Row className="g-3">
                      <Col md={6}>
                        <FieldRow label="Form Heading">
                          <Form.Control
                            value={cu.formTitle}
                            placeholder="Send Us a Message"
                            onChange={e => set('formTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Submit Button Text">
                          <Form.Control
                            value={cu.formButtonText}
                            placeholder="Send Message"
                            onChange={e => set('formButtonText', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Success Title (after submit)">
                          <Form.Control
                            value={cu.formSuccessTitle}
                            placeholder="Message Sent!"
                            onChange={e => set('formSuccessTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Success Message">
                          <Form.Control
                            value={cu.formSuccessText}
                            placeholder="We'll get back to you within 24 hours."
                            onChange={e => set('formSuccessText', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 6. MAP ──────────────────────────────────── */}
                <Accordion.Item eventKey="5">
                  <Accordion.Header>
                    <i className="ri-map-2-line me-2 text-primary" />
                    <strong>Google Maps Embed</strong>
                    <span className="ms-2 text-muted small">— Embed iframe shown below the form</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="6. Map Embed" />
                    <FieldRow label="Google Maps Embed URL">
                      <Form.Control
                        value={cu.mapIframeUrl}
                        placeholder="https://www.google.com/maps/embed?pb=..."
                        onChange={e => set('mapIframeUrl', e.target.value)}
                      />
                    </FieldRow>
                    <small className="text-muted">
                      Go to Google Maps → Share → Embed a map → copy the <code>src</code> URL from the iframe code.
                    </small>
                    {cu.mapIframeUrl && (
                      <div className="mt-3 rounded overflow-hidden border">
                        <iframe
                          src={cu.mapIframeUrl}
                          width="100%"
                          height="200"
                          style={{ border: 0, display: 'block' }}
                          allowFullScreen
                          loading="lazy"
                          title="Map preview"
                        />
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 7. EXTRA SECTIONS ───────────────────────── */}
                <Accordion.Item eventKey="6">
                  <Accordion.Header>
                    <i className="ri-layout-grid-line me-2 text-primary" />
                    <strong>Extra Sections</strong>
                    <span className="ms-2 text-muted small">— Branch offices, regional info etc.</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="7. Custom Sections" />
                    <p className="text-muted small mb-3">
                      Add branch offices, regional contact info, whatsapp details, or any other content block.
                    </p>
                    {(cu.extraSections || []).map((s, i) => (
                      <ItemCard key={i} onRemove={() => removeItem('extraSections', i)}>
                        <Row className="g-2">
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Section Label (small caps)</Form.Label>
                            <Form.Control
                              size="sm"
                              value={s.label || ''}
                              placeholder="MUMBAI OFFICE"
                              onChange={e => updateItem('extraSections', i, 'label', e.target.value)}
                            />
                          </Col>
                          <Col md={8}>
                            <Form.Label className="small fw-bold">Section Heading</Form.Label>
                            <Form.Control
                              size="sm"
                              value={s.title || ''}
                              placeholder="Visit Us in Mumbai"
                              onChange={e => updateItem('extraSections', i, 'title', e.target.value)}
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Label className="small fw-bold">Content / Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={s.description || ''}
                              placeholder="Address, contact details, timings..."
                              onChange={e => updateItem('extraSections', i, 'description', e.target.value)}
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Label className="small fw-bold">Image URL (optional)</Form.Label>
                            <Form.Control
                              size="sm"
                              value={s.image || ''}
                              placeholder="https://..."
                              onChange={e => updateItem('extraSections', i, 'image', e.target.value)}
                            />
                          </Col>
                        </Row>
                      </ItemCard>
                    ))}
                    <AddBtn onClick={() => addItem('extraSections', emptySection)} label="+ Add Section" />
                  </Accordion.Body>
                </Accordion.Item>

              </Accordion>
            </Card.Body>

            {/* ── SAVE FOOTER ── */}
            <Card.Footer className="bg-white border-top py-3">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  <i className="ri-information-line me-1 text-primary" />
                  Click Save to publish Contact Us content to the database.
                </small>
                <Button
                  variant="primary"
                  className="px-5 fw-bold shadow"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'SAVE CONTACT US ✦'}
                </Button>
              </div>
            </Card.Footer>

          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ContactUsEditor;